export type BillStatusCategory = 'passed' | 'failed' | 'vetoed';

export type LobbyistStatusFilter = 'current' | 'all' | 'inactive' | 'suspended' | 'terminated' | 'expired';

export interface LobbyingBillFilterOptions {
  clientId?: string;
  clientName?: string;
  lobbyistId?: string;
  lobbyistName?: string;
  lobbyistStatus?: LobbyistStatusFilter | string;
}

export interface BillFilterParams {
  legislator_id?: string;
  sponsor?: string;
  bill_status?: BillStatusCategory;
  bipartisan?: string;
}

/**
 * Maps bill status categories to the backend filter values
 * Based on the patterns used in the legislator stats API
 */
export const BILL_STATUS_PATTERNS = {
  passed: [
    'signed by governor',
    'governor signed',
    'became law'
  ],
  failed: [
    'failed',
    'postponed indefinitely',
    'killed'
  ],
  vetoed: [
    'vetoed'
  ]
} as const;

/**
 * Creates URL search parameters for filtering bills by legislator and status
 */
export const createBillFilterUrl = (
  legislatorId: string,
  category?: BillStatusCategory,
  options: { bipartisan?: boolean; sessionYear?: string | number } = {}
): string => {
  const params = new URLSearchParams();
  
  // Always filter by legislator as sponsor
  params.set('legislator_id', legislatorId);
  // Set chamber to All to include both House and Senate bills
  params.set('chamber', 'All');
  
  // Add session year if specified (important for matching legislator's session)
  if (options.sessionYear) {
    params.set('session', String(options.sessionYear));
  }
  
  // Add status category if specified
  if (category) {
    params.set('bill_status', category);
  }
  
  // Add bipartisan filter if specified
  if (options.bipartisan) {
    params.set('bipartisan', 'true');
  }
  
  return `/legislation?${params.toString()}`;
};

/**
 * Creates URL for filtering bills by lobbying client and/or lobbyist.
 * Mirrors createBillFilterUrl — used by ClientsTab and LobbyistsTab bill count links.
 */
export const createLobbyingBillFilterUrl = (options: LobbyingBillFilterOptions): string => {
  const params = new URLSearchParams();

  if (options.clientId) {
    params.set('client_id', options.clientId);
  }
  if (options.clientName) {
    params.set('client_name', options.clientName);
  }
  if (options.lobbyistId) {
    params.set('lobbyist_id', options.lobbyistId);
  }
  if (options.lobbyistName) {
    params.set('lobbyist_name', options.lobbyistName);
  }
  if (options.lobbyistStatus && options.lobbyistStatus !== 'all') {
    params.set('lobbyist_status', options.lobbyistStatus);
  }

  return `/legislation?${params.toString()}`;
};

/** User-facing label for lobbyist_status deep-link suffixes */
export const getLobbyistStatusLabel = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'current':
      return 'Current lobbyists only';
    case 'inactive':
      return 'Inactive lobbyists only';
    case 'suspended':
      return 'Suspended lobbyists only';
    case 'terminated':
      return 'Terminated lobbyists only';
    case 'expired':
      return 'Expired lobbyists only';
    default:
      return `${status} lobbyists only`;
  }
};

/**
 * Helper function to get user-friendly labels for bill status categories
 */
export const getBillStatusLabel = (category: BillStatusCategory): string => {
  switch (category) {
    case 'passed':
      return 'Passed Bills';
    case 'failed':
      return 'Failed Bills';
    case 'vetoed':
      return 'Vetoed Bills';
    default:
      return 'Bills';
  }
};

/**
 * Helper function to get page titles for filtered bill views
 */
export const getBillPageTitle = (
  legislatorName: string,
  category?: BillStatusCategory,
  bipartisan = false
): string => {
  if (bipartisan) {
    return `Bipartisan Bills Sponsored by ${legislatorName}`;
  }
  
  if (category) {
    return `${getBillStatusLabel(category)} Sponsored by ${legislatorName}`;
  }
  
  return `Bills Sponsored by ${legislatorName}`;
};
